'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import {
  List, X, CaretDown, CaretRight,
} from '@phosphor-icons/react'
import NavDropdownSolutions, { INDUSTRIES as INDUSTRIES_M, PRODUCTS, PRODUCT_ICONS, TEAMS } from './NavDropdownSolutions'
import NavDropdownResources, { LIBRARY_ITEMS as RESOURCES_LIB_M } from './NavDropdownResources'
import NavDropdownCompany, { COMPANY_ITEMS as COMPANY_ITEMS_M } from './NavDropdownCompany'
import NavDropdownPartner, { PARTNERS as PARTNERS_M } from './NavDropdownPartner'
import { SubscribeSlideOver } from './SubscribeSlideOver'

type DropdownKey = 'Solutions' | 'Resources' | 'Company' | 'Partner' | null

interface NavBarMainProps {
  theme?: 'light' | 'dark'
  ctaLabel?: string
  ctaHref?: string
  secondaryLabel?: string
  secondaryHref?: string
  eventCta?: string
  eventCtaHref?: string
  storiesHref?: string
}

const LIGHT_FILL = '#000A1B'
const DARK_FILL  = '#F0F0F0'

// Teams grid is 3 columns x 4 rows for the desktop dropdown — flatten to a single
// list of 12 for the mobile accordion, which just stacks them.
const TEAMS_FLAT = TEAMS.flat()

// ── SVGs ──────────────────────────────────────────────────────────────────────
const WordmarkSvg = ({ fill }: { fill: string }) => (
  <svg style={{ height: '33px', width: 'auto' }} viewBox="0 0 208 34" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M200.327 12.7534C200.633 12.8215 200.804 12.5152 200.914 12.2685C201.978 9.85223 202.999 8.57604 207.355 8.57604V13.885C202.386 13.5362 200.778 15.3654 200.778 19.9937V27.0637H195.299V8.8568H200.778V9.68207C200.778 10.3031 200.574 10.9242 200.302 11.4772C200.029 12.0303 199.825 12.6513 200.336 12.7534H200.327Z" fill={fill}/>
    <path d="M193.861 17.084C193.861 17.586 193.827 18.4708 193.759 18.9727H180.461C180.844 21.2358 182.41 22.7503 184.732 22.7503C186.468 22.7503 187.54 21.908 187.999 20.5892H193.521C192.789 23.9584 189.948 27.0638 184.775 27.0638C178.385 27.0638 174.982 22.8779 174.982 17.62C174.982 12.0898 178.522 8.1762 184.673 8.1762C189.327 8.1762 193.878 10.9413 193.878 17.084H193.861ZM184.766 12.3621C182.129 12.3621 181.014 13.8084 180.597 15.7312H188.408C188.195 13.5702 186.808 12.3621 184.758 12.3621H184.766Z" fill={fill}/>
    <path d="M98.3852 8.98441H104.196L107.446 20.6317C107.616 21.2783 107.684 22.2738 107.684 23.065C107.684 23.4053 107.752 23.8222 108.229 23.8222C108.705 23.8222 108.773 23.4138 108.773 23.065C108.773 22.2738 108.841 21.2869 109.012 20.6317L112.262 8.98441H118.073L112.057 27.0722H104.4L98.3852 8.98441Z" fill={fill}/>
    <path d="M117.511 21.7718C117.511 17.7561 120.735 16.2757 124.632 16.0375C127.848 15.8333 130.145 16.0375 130.111 14.5231C130.077 13.5107 129.524 12.226 127.031 12.226C124.538 12.226 123.739 13.3065 123.466 14.6507H118.055C118.668 9.8948 123.126 8.1762 127.167 8.1762C131.447 8.1762 135.59 9.52896 135.59 16.6756V26.5278H130.111V25.6174C130.111 25.0814 130.247 24.605 130.486 24.137C130.758 23.567 131.064 23.0225 130.69 22.8523C130.247 22.6482 130.111 23.0565 129.967 23.2948C128.835 25.4898 126.819 27.0723 123.798 27.0723C120.131 27.0723 117.494 24.7411 117.494 21.7804L117.511 21.7718ZM130.12 17.8922C129.226 18.8026 127.959 18.8706 125.389 19.1769C124.122 19.3131 122.99 19.8831 122.99 21.406C122.99 22.9289 124.428 23.533 125.9 23.2948C127.542 23.0225 130.077 21.5762 130.111 17.9007L130.12 17.8922Z" fill={fill}/>
    <path d="M89.3417 8.1762C95.2122 8.1762 98.7855 12.4302 98.7855 17.62C98.7855 22.8098 95.2122 27.0638 89.3417 27.0638C83.4713 27.0638 79.8979 22.8098 79.8979 17.62C79.8979 12.4302 83.4713 8.1762 89.3417 8.1762ZM89.3417 22.478C92.0047 22.478 93.3915 20.2489 93.3915 17.62C93.3915 14.991 92.0132 12.762 89.3417 12.762C86.6702 12.762 85.292 14.991 85.292 17.62C85.292 20.2489 86.6787 22.478 89.3417 22.478Z" fill={fill}/>
    <path d="M71.7809 8.1762C75.686 8.1762 78.2894 10.7456 78.2894 15.9524V27.0638H72.8103V18.173C72.8103 14.0382 71.883 12.8981 69.9347 12.8981C67.7056 12.8981 65.6892 15.7823 65.6892 20.0192V27.0638H60.2101V8.73772H65.6892V9.60553C65.6892 10.4393 65.5191 11.0264 65.2468 11.5879C64.9745 12.1409 64.6342 12.8385 65.0766 13.0087C65.485 13.1789 65.6552 12.762 65.7998 12.4897C66.6591 10.652 68.6755 8.18471 71.7979 8.18471L71.7809 8.1762Z" fill={fill}/>
    <path d="M51.6859 8.1762C55.591 8.1762 58.1945 10.7456 58.1945 15.9524V27.0638H52.7154V18.173C52.7154 14.0382 51.788 12.8981 49.8397 12.8981C47.6106 12.8981 45.5942 15.7823 45.5942 20.0192V27.0638H40.1151V8.73772H45.5942V9.60553C45.5942 10.4393 45.4241 11.0264 45.1518 11.5879C44.8796 12.1409 44.5393 12.8385 44.9817 13.0087C45.39 13.1789 45.5602 12.762 45.7048 12.4897C46.5641 10.652 48.5805 8.18471 51.7029 8.18471L51.6859 8.1762Z" fill={fill}/>
    <path d="M32.4746 6.90843V2.55237H38.0984V6.90843H32.4746ZM32.4746 27.0637V9.08645H38.0984V27.0637H32.4746Z" fill={fill}/>
    <path d="M156.086 17.62C156.086 12.4216 159.515 8.1762 165.53 8.1762C170.847 8.1762 173.91 11.8856 174.174 15.7653L168.925 15.7993C168.627 14.1488 167.495 12.762 165.564 12.762C163.003 12.762 161.404 14.991 161.404 17.62C161.404 20.2489 163.003 22.478 165.564 22.478C167.495 22.478 168.627 21.0572 168.925 19.4066L174.174 19.4407C173.91 23.3203 170.881 27.0638 165.53 27.0638C159.608 27.0638 156.086 22.8098 156.086 17.62Z" fill={fill}/>
    <path d="M137.122 17.62C137.122 12.4216 140.551 8.1762 146.566 8.1762C151.883 8.1762 154.946 11.8856 155.21 15.7653L149.96 15.7993C149.663 14.1488 148.531 12.762 146.6 12.762C144.039 12.762 142.439 14.991 142.439 17.62C142.439 20.2489 144.039 22.478 146.6 22.478C148.531 22.478 149.663 21.0572 149.96 19.4066L155.21 19.4407C154.946 23.3203 151.917 27.0638 146.566 27.0638C140.644 27.0638 137.122 22.8098 137.122 17.62Z" fill={fill}/>
    <path d="M8.36276 18.7476L0 33.1253L16.6313 33.1768L8.36276 18.7476Z" fill={fill}/>
    <path d="M8.3798 14.4292L16.7426 0.0514104L0.111289 0L8.3798 14.4292Z" fill={fill}/>
    <path d="M10.2478 15.5089L26.879 15.5689L18.6105 1.13966L10.2478 15.5089Z" fill={fill}/>
    <path d="M10.2394 17.6765L18.5079 32.1057L26.8707 17.7279L10.2394 17.6765Z" fill={fill}/>
  </svg>
)

const IconMarkSvg = ({ fill }: { fill: string }) => (
  <svg style={{ height: '36px', width: 'auto' }} viewBox="0 0 28 34" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.36276 18.7476L0 33.1253L16.6313 33.1768L8.36276 18.7476Z" fill={fill}/>
    <path d="M8.3798 14.4292L16.7426 0.0514104L0.111289 0L8.3798 14.4292Z" fill={fill}/>
    <path d="M10.2478 15.5089L26.879 15.5689L18.6105 1.13966L10.2478 15.5089Z" fill={fill}/>
    <path d="M10.2394 17.6765L18.5079 32.1057L26.8707 17.7279L10.2394 17.6765Z" fill={fill}/>
  </svg>
)

// ── Component ─────────────────────────────────────────────────────────────────
export function NavBarMain({
  theme = 'light',
  ctaLabel = 'REQUEST A DEMO',
  ctaHref = '/demo',
  secondaryLabel,
  secondaryHref,
  eventCta,
  eventCtaHref = '#',
  storiesHref = '/blogs',
}: NavBarMainProps) {
  const [openDropdown,   setOpenDropdown]   = useState<DropdownKey>(null)
  const [subscribeOpen,  setSubscribeOpen]  = useState(false)
  const [mobileOpen,     setMobileOpen]     = useState(false)
  // Exclusive accordion — opening one mobile section collapses whichever
  // other section was open. A section also closes if its own row is clicked again.
  const [mobileExpanded, setMobileExpanded] = useState<DropdownKey>(null)
  const [mounted,        setMounted]        = useState(false)

  useEffect(() => { setMounted(true) }, [])

  // Solutions panel position (viewport-relative for position:fixed portal)
  const [solLeft,   setSolLeft]   = useState(0)
  const [solRight,  setSolRight]  = useState(0)
  // Fit panel position (viewport-relative)
  const [fitStyle,  setFitStyle]  = useState<React.CSSProperties>({ left: 0 })
  // Top Y of panel — bottom edge of the nav bar
  const [panelTop,  setPanelTop]  = useState(0)

  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wrapRef       = useRef<HTMLDivElement>(null)  // position:relative container
  const innerRef      = useRef<HTMLDivElement>(null)  // nav-bar__inner — for Solutions bounds
  const resTriggerRef = useRef<HTMLDivElement>(null)
  const comTriggerRef = useRef<HTMLDivElement>(null)
  const parTriggerRef = useRef<HTMLDivElement>(null)

  const openPanel = useCallback((name: NonNullable<DropdownKey>) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)

    // Capture the nav bar's bottom edge for the portal's fixed position
    if (wrapRef.current) {
      setPanelTop(Math.round(wrapRef.current.getBoundingClientRect().bottom))
    }

    if (name === 'Solutions') {
      // Align panel to the nav-bar__inner content bounds — viewport-relative
      if (innerRef.current) {
        const innerRect = innerRef.current.getBoundingClientRect()
        setSolLeft(Math.round(innerRect.left))
        setSolRight(Math.round(window.innerWidth - innerRect.right))
      }
    } else {
      // Resources / Company / Partner panels are centred in the viewport
      setFitStyle({ left: '50%', transform: 'translateX(-50%)' })
    }
    setOpenDropdown(name)
  }, [])

  const scheduleClose = useCallback(() => {
    closeTimerRef.current = setTimeout(() => setOpenDropdown(null), 150)
  }, [])

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
  }, [])

  const closeMobile = useCallback(() => {
    setMobileOpen(false)
    setMobileExpanded(null)
  }, [])

  const toggleMobileItem = useCallback((name: NonNullable<DropdownKey>) => {
    setMobileExpanded(prev => prev === name ? null : name)
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpenDropdown(null); closeMobile() }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [closeMobile])

  useEffect(() => {
    if (!mobileOpen) return
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) closeMobile()
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [mobileOpen, closeMobile])

const fill       = theme === 'dark' ? DARK_FILL : LIGHT_FILL
  const ctaVariant = theme === 'dark' ? 'block-button--limestone' : 'block-button--midnight'

  // Panels are portalled to document.body using position:fixed so no ancestor
  // overflow or stacking context can ever clip them.
  const panelBase: React.CSSProperties = {
    position: 'fixed', top: panelTop, zIndex: 1001, paddingTop: '8px',
  }

  return (
    <>
    <header style={{ position: 'sticky', top: 0, zIndex: 1000, overflow: 'visible' }}>
      <div ref={wrapRef} style={{ position: 'relative', overflow: 'visible' }}>

        {/* ── Desktop nav bar ── */}
        <div className={`nav-bar nav-bar--desktop nav-bar--${theme}`}>
          <div className="nav-bar__inner" ref={innerRef}>
            <div className="nav-bar__left">
              <a href="https://innovaccer.com" className="nav-bar__logo" style={{ textDecoration: 'none' }}>
                <IconMarkSvg fill={fill} />
              </a>
              <nav className="nav-bar__center">
                <div onMouseEnter={() => openPanel('Solutions')} onMouseLeave={scheduleClose}>
                  <div className={`nav-bar__item${openDropdown === 'Solutions' ? ' nav-bar__item--active' : ''}`}>Solutions</div>
                </div>
                <a href="https://innovaccer.com/customer/customer-stories" className="nav-bar__item" style={{ textDecoration: 'none' }}>Customers</a>
                <Link href={storiesHref} className="nav-bar__item" style={{ textDecoration: 'none' }}>Stories</Link>
                <div ref={resTriggerRef} onMouseEnter={() => openPanel('Resources')} onMouseLeave={scheduleClose}>
                  <div className={`nav-bar__item${openDropdown === 'Resources' ? ' nav-bar__item--active' : ''}`}>Resources</div>
                </div>
                <div ref={comTriggerRef} onMouseEnter={() => openPanel('Company')} onMouseLeave={scheduleClose}>
                  <div className={`nav-bar__item${openDropdown === 'Company' ? ' nav-bar__item--active' : ''}`}>Company</div>
                </div>
                <div ref={parTriggerRef} onMouseEnter={() => openPanel('Partner')} onMouseLeave={scheduleClose}>
                  <div className={`nav-bar__item${openDropdown === 'Partner' ? ' nav-bar__item--active' : ''}`}>Partner</div>
                </div>
                {eventCta && (
                  <a href={eventCtaHref} className="nav-bar__cta">
                    <span className="nav-bar__cta__text">
                      <span className="btn-primary-text">{eventCta}</span>
                      <span className="btn-hover-text">{eventCta}</span>
                    </span>
                  </a>
                )}
              </nav>
            </div>
            <div className="nav-bar__right">
              {secondaryLabel && secondaryHref && (
                <a href={secondaryHref} className="block-button-link">
                  <div className={`block-button block-button--md ${theme === 'dark' ? 'block-button--ghost-dark' : 'block-button--ghost'}`}>
                    <div className="block-button__text">
                      <span className="btn-primary-text">{secondaryLabel}</span>
                      <span className="btn-hover-text">{secondaryLabel}</span>
                    </div>
                  </div>
                </a>
              )}
              <a
                href="https://innovaccer.com/request-a-demo"
                className="block-button-link nav-bar__cta--hide-mobile"
                style={{ textDecoration: 'none' }}
              >
                <div className={`block-button block-button--md ${ctaVariant}`} style={{ padding: '13px 15px', fontSize: 12, letterSpacing: '1.08px', borderRadius: 4, fontWeight: 600 }}>
                  <div className="block-button__text">
                    <span className="btn-primary-text">{ctaLabel}</span>
                    <span className="btn-hover-text">{ctaLabel}</span>
                  </div>
                </div>
              </a>
              <button
                className="nav-bar__hamburger"
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileOpen}
                onClick={() => { setOpenDropdown(null); setMobileOpen(o => !o); if (mobileOpen) setMobileExpanded(null) }}
                onMouseEnter={() => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current); setOpenDropdown(null); setMobileOpen(true) }}
                onMouseLeave={() => { closeTimerRef.current = setTimeout(() => { setMobileOpen(false); setMobileExpanded(null) }, 150) }}
              >
                {mobileOpen ? <X size={20} /> : <List size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* ── Mobile drawer ── (panels are portalled separately below) ── */}

        {/* ── Mobile drawer ── */}
        <div
          className={`nav-mobile-menu${mobileOpen ? ' is-open' : ''}`}
          onMouseEnter={() => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current) }}
          onMouseLeave={() => { closeTimerRef.current = setTimeout(() => { setMobileOpen(false); setMobileExpanded(null) }, 150) }}
        >

          {/* Solutions */}
          <div className="nav-mobile-menu__item">
            <button className="nav-mobile-menu__item-row" aria-expanded={mobileExpanded === 'Solutions'} onClick={() => toggleMobileItem('Solutions')}>
              <span className="nav-mobile-menu__item-label">Solutions</span>
              <span className="nav-mobile-menu__caret"><CaretDown size={16} /></span>
            </button>
            <div className={`nav-mobile-menu__submenu${mobileExpanded === 'Solutions' ? ' is-open' : ''}`}>
              <div className="nav-mobile-menu__sub-group">
                <div className="nav-mobile-menu__sub-label">Industries</div>
                {INDUSTRIES_M.map(({ label, icon: Icon, href }) => (
                  <a key={label} href={href} className="nav-mobile-menu__industry-card">
                    <div className="nav-mobile-menu__industry-card__inner">
                      <div className="nav-mobile-menu__industry-icon" style={{ background: 'transparent' }}><Icon /></div>
                      <span className="nav-mobile-menu__industry-label">{label}</span>
                    </div>
                    <CaretRight size={12} />
                  </a>
                ))}
              </div>
              <div className="nav-mobile-menu__sub-group">
                <div className="nav-mobile-menu__sub-label">Solutions</div>
                <div className="nav-mobile-menu__product-grid">
                  {PRODUCTS.map(({ name, href }) => (
                    <a key={name} href={href} className="nav-mobile-menu__product-card">
                      <div className="nav-mobile-menu__product-icon"><img src={PRODUCT_ICONS[name]} alt={name} /></div>
                      <span className="nav-mobile-menu__product-label">{name}</span>
                    </a>
                  ))}
                </div>
              </div>
              <div className="nav-mobile-menu__sub-group">
                <div className="nav-mobile-menu__sub-label">Teams</div>
                {TEAMS_FLAT.map(({ label, href }) => (
                  <a key={label} href={href} className="nav-mobile-menu__team-item">
                    <span>{label}</span><CaretRight size={12} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Customers — direct link */}
          <div className="nav-mobile-menu__item">
            <a href="https://innovaccer.com/customer/customer-stories" className="nav-mobile-menu__item-row" style={{ textDecoration: 'none' }} onClick={closeMobile}>
              <span className="nav-mobile-menu__item-label">Customers</span>
            </a>
          </div>

          {/* Stories — direct link */}
          <div className="nav-mobile-menu__item">
            <Link href={storiesHref} className="nav-mobile-menu__item-row" style={{ textDecoration: 'none' }} onClick={closeMobile}>
              <span className="nav-mobile-menu__item-label">Stories</span>
            </Link>
          </div>

          {/* Resources */}
          <div className="nav-mobile-menu__item">
            <button className="nav-mobile-menu__item-row" aria-expanded={mobileExpanded === 'Resources'} onClick={() => toggleMobileItem('Resources')}>
              <span className="nav-mobile-menu__item-label">Resources</span>
              <span className="nav-mobile-menu__caret"><CaretDown size={16} /></span>
            </button>
            <div className={`nav-mobile-menu__submenu${mobileExpanded === 'Resources' ? ' is-open' : ''}`}>
              <div className="nav-mobile-menu__sub-group">
                <div className="nav-mobile-menu__sub-label">Library</div>
                {RESOURCES_LIB_M.map(({ label, icon: Icon, href, external, transparentBg }) => (
                  <a
                    key={label}
                    href={href}
                    className="nav-mobile-menu__resource-item"
                    {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  >
                    <div className="nav-mobile-menu__resource-icon" style={transparentBg ? { background: 'transparent' } : undefined}><Icon /></div>
                    <span>{label}</span>
                  </a>
                ))}
              </div>
              <div className="nav-mobile-menu__sub-group">
                <div className="nav-mobile-menu__sub-label">Featured</div>
                <a href="https://innovaccer.com/science-of-value-based-care" target="_blank" rel="noopener noreferrer" className="nav-mobile-menu__featured-item">
                  <span className="nav-mobile-menu__featured-title">NAACOS and Innovaccer Latest Report Shows 64% of Healthcare Leaders Expect Revenue Gains from Value-Based Care in 2025</span>
                  <CaretRight size={12} style={{ flexShrink: 0 }} />
                </a>
              </div>
            </div>
          </div>

          {/* Company */}
          <div className="nav-mobile-menu__item">
            <button className="nav-mobile-menu__item-row" aria-expanded={mobileExpanded === 'Company'} onClick={() => toggleMobileItem('Company')}>
              <span className="nav-mobile-menu__item-label">Company</span>
              <span className="nav-mobile-menu__caret"><CaretDown size={16} /></span>
            </button>
            <div className={`nav-mobile-menu__submenu${mobileExpanded === 'Company' ? ' is-open' : ''}`}>
              <div className="nav-mobile-menu__sub-group">
                <div className="nav-mobile-menu__sub-label">Company</div>
                {COMPANY_ITEMS_M.map(({ label, icon: Icon, href }) => (
                  <a key={label} href={href} className="nav-mobile-menu__resource-item">
                    <div className="nav-mobile-menu__resource-icon" style={{ background: 'transparent' }}><Icon /></div>
                    <span>{label}</span>
                  </a>
                ))}
              </div>
              <div className="nav-mobile-menu__sub-group">
                <div className="nav-mobile-menu__sub-label">Featured</div>
                <a href="https://www.linkedin.com/pulse/we-dont-sell-healthcare-platforms-here-abhinav-shashan" target="_blank" rel="noopener noreferrer" className="nav-mobile-menu__featured-item">
                  <span className="nav-mobile-menu__featured-title">We Don't Sell Healthcare Platforms Here</span>
                  <CaretRight size={12} style={{ flexShrink: 0 }} />
                </a>
              </div>
            </div>
          </div>

          {/* Partner */}
          <div className="nav-mobile-menu__item">
            <button className="nav-mobile-menu__item-row" aria-expanded={mobileExpanded === 'Partner'} onClick={() => toggleMobileItem('Partner')}>
              <span className="nav-mobile-menu__item-label">Partner</span>
              <span className="nav-mobile-menu__caret"><CaretDown size={16} /></span>
            </button>
            <div className={`nav-mobile-menu__submenu${mobileExpanded === 'Partner' ? ' is-open' : ''}`}>
              <div className="nav-mobile-menu__sub-group">
                <div className="nav-mobile-menu__sub-label">Our Partners</div>
                {PARTNERS_M.map(({ label, logo, href }) => (
                  <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="nav-mobile-menu__resource-item">
                    <div className="nav-mobile-menu__resource-icon" style={{ background: 'transparent' }}>
                      <img src={logo} alt={label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </div>
                    <span>{label}</span>
                  </a>
                ))}
              </div>
              <div className="nav-mobile-menu__sub-group">
                <div className="nav-mobile-menu__sub-label">Announcements</div>
                <a href="https://innovaccer.com/partnerships/mastek" target="_blank" rel="noopener noreferrer" className="nav-mobile-menu__featured-item">
                  <span className="nav-mobile-menu__featured-title">Delivering Autonomous Healthcare Operations at Global Scale</span>
                  <CaretRight size={12} style={{ flexShrink: 0 }} />
                </a>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="nav-mobile-menu__ctas">
            {eventCta && (
              <a
                href={eventCtaHref}
                style={{ width: '100%', textDecoration: 'none' }}
                onClick={closeMobile}
              >
                <div className={`block-button block-button--md ${ctaVariant}`} style={{ width: '100%', justifyContent: 'center' }}>
                  <div className="block-button__text">
                    <span className="btn-primary-text">{eventCta}</span>
                    <span className="btn-hover-text">{eventCta}</span>
                  </div>
                </div>
              </a>
            )}
            {secondaryLabel && secondaryHref && (
              <a
                href={secondaryHref}
                style={{ width: '100%', textDecoration: 'none' }}
                onClick={closeMobile}
              >
                <div className={`block-button block-button--md ${theme === 'dark' ? 'block-button--ghost-dark' : 'block-button--ghost'}`} style={{ width: '100%', justifyContent: 'center' }}>
                  <div className="block-button__text">
                    <span className="btn-primary-text">{secondaryLabel}</span>
                    <span className="btn-hover-text">{secondaryLabel}</span>
                  </div>
                </div>
              </a>
            )}
            <a
              href="https://innovaccer.com/request-a-demo"
              style={{ width: '100%', textDecoration: 'none' }}
              onClick={closeMobile}
            >
              <div className={`block-button block-button--md ${ctaVariant}`} style={{ width: '100%', justifyContent: 'center' }}>
                <div className="block-button__text">
                  <span className="btn-primary-text">{ctaLabel}</span>
                  <span className="btn-hover-text">{ctaLabel}</span>
                </div>
              </div>
            </a>
          </div>
        </div>

      </div>

      <SubscribeSlideOver
        open={subscribeOpen}
        onClose={() => setSubscribeOpen(false)}
        source="nav-demo"
      />
    </header>

    {/* ── Blur backdrop — portalled so it never clips the nav bar shadow ── */}
    {mounted && openDropdown && createPortal(
      <div
        style={{
          position: 'fixed',
          inset: 0,
          top: panelTop,
          zIndex: 999,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          background: 'rgba(0,0,0,0.15)',
          pointerEvents: 'auto',
        }}
        onClick={() => setOpenDropdown(null)}
      />,
      document.body
    )}

    {/* ── Desktop dropdown panels — portalled to body so no ancestor clips them ── */}
    {mounted && openDropdown === 'Solutions' && createPortal(
      <div style={{ ...panelBase, left: solLeft, right: solRight }} onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
        <NavDropdownSolutions />
      </div>,
      document.body
    )}
    {mounted && openDropdown === 'Resources' && createPortal(
      <div style={{ ...panelBase, ...fitStyle }} onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
        <NavDropdownResources />
      </div>,
      document.body
    )}
    {mounted && openDropdown === 'Company' && createPortal(
      <div style={{ ...panelBase, ...fitStyle }} onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
        <NavDropdownCompany />
      </div>,
      document.body
    )}
    {mounted && openDropdown === 'Partner' && createPortal(
      <div style={{ ...panelBase, ...fitStyle }} onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
        <NavDropdownPartner />
      </div>,
      document.body
    )}
  </>
  )
}
